/**
 * What Reeve states on the plan instead of asking.
 *
 * A decision question with a recommendation is not a question Reeve needs
 * answered: people kept the recommendation 9 times in 10. So it reads as a
 * line, "Reeve decided: ship as its own PR", and approving the plan accepts
 * it. A fact Reeve read and answered reads "Reeve checked", with what it read;
 * one it planned on reads "Reeve assumed", with the step that checks it first
 * (`statedKind`), or that changes nothing in the plan, with the source it
 * could not read (YAG-999). Change opens the options in place, each with its reason
 * and what picking it changes in the plan; picking another, or typing
 * something else and saving it, makes the line "You chose" with Undo, that
 * option's own reason, and where the answer stands in the plan: Reeve writes
 * it in as a new version before Approve is offered (spec 2026-11-02). Ask
 * opens the plan-review chat on that one line, as a question card's Ask does,
 * so "why this?" is answered, never a send-back (ENG-2966).
 *
 * A pick is saved on the server as it is made; what is still Reeve's at
 * approval is recorded by the server as Reeve's, accepted by the approver.
 */
import { useState } from "react";
import {
  statedKind,
  unreadSourceOf,
  type PlanInputView,
  type PlanQuestionOption,
  type StatedKind,
} from "@yagni/shared";
import type { PlanQuestionRow } from "../work/planQuestions.js";
import { Btn, cx } from "../queue/index.js";
import { PLAN_ANSWER_SAVE_VERB } from "../work/workCopy.js";
import { Textarea } from "../ui/textarea.js";
import { CheckedLines, OPTION_TEXTAREA, OptionText } from "./QuestionCards.js";
import { isPendingInput, PlanInputStatus } from "./PlanAnswerLines.js";

export interface StatedDecisionsProps {
  /** What the plan states: decisions, and facts Reeve checked or assumed (`isStatedQuestion`). */
  questions: readonly PlanQuestionRow[];
  /** The live answers saved on the gate, one per question at most. */
  answers: readonly PlanInputView[];
  onAnswer: (question: PlanQuestionRow, answer: string, why: string) => void;
  /** Back to Reeve's decision: the person's answer is withdrawn. */
  onUndo: (questionId: string) => void;
  /** Retry an answer that could not be written into the plan. */
  onRetry?: (inputId: string) => void;
  /** Start over with an answer the plan cannot take. */
  onStartOver?: (input: PlanInputView) => void;
  /** Keep an answer that could not be written into the plan as its decision line. */
  onKeepAsDecision?: (inputId: string) => void;
  /** Ask the plan-review chat about this line. */
  onAsk?: (question: PlanQuestionRow) => void;
  /** Who decided ("Reeve"). */
  askerName: string;
}

const VERB: Record<StatedKind, string> = {
  decision: "decided",
  checked: "checked",
  assumed: "assumed",
};

/** The step or check an assumption is tested by first: what approving adds to the plan. */
function safeguardOf(question: PlanQuestionRow): string | null {
  const change = question.recommendation?.planChange;
  const step = change?.addSteps?.[0];
  return step ? (step.title ?? step.description) : (change?.addChecks?.[0] ?? null);
}

function optionsOf(question: PlanQuestionRow): Array<PlanQuestionOption & { rec: boolean }> {
  return [
    ...(question.recommendation ? [{ ...question.recommendation, rec: true }] : []),
    ...question.alternatives.map((option) => ({ ...option, rec: false })),
  ];
}

export function StatedDecisions({
  questions,
  answers,
  onAnswer,
  onUndo,
  onRetry,
  onStartOver,
  onKeepAsDecision,
  onAsk,
  askerName,
}: StatedDecisionsProps) {
  const [open, setOpen] = useState<string | null>(null);
  // What the reader is typing in "Something else", as typed, until it is
  // saved with Use this answer: nothing is sent while they type.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const setDraft = (questionId: string, text: string | null) =>
    setDrafts((prev) => {
      const { [questionId]: _dropped, ...rest } = prev;
      return text === null ? rest : { ...rest, [questionId]: text };
    });
  if (questions.length === 0) return null;
  const kinds = new Set(questions.map((question) => statedKind(question)));
  const heading =
    kinds.size === 1 && kinds.has("decision")
      ? "decided"
      : kinds.size === 1 && kinds.has("checked")
        ? "checked"
        : "settled";
  return (
    <div className="blk" data-testid="stated-decisions">
      <div className="bh">
        <span className="label">
          {askerName} {heading}
        </span>
        <span className="fact">{questions.length} · change any before you approve</span>
      </div>
      <div className="decided">
        {questions.map((question) => {
          const saved = answers.find((entry) => entry.questionId === question.id) ?? null;
          // Reeve's own Skip is still Reeve's decision; only a person's answer is "You chose".
          const mine = saved && !saved.skipped && saved.kind === "answer" ? saved : null;
          const current = mine?.body ?? question.recommendation?.answer ?? "";
          const options = optionsOf(question);
          const typed = mine != null && !options.some((option) => option.answer === mine.body);
          const savedText = typed ? (mine?.body ?? "") : "";
          const draft = drafts[question.id];
          const typedText = draft ?? savedText;
          const canSave = draft !== undefined && draft.trim() !== "" && draft.trim() !== mine?.body;
          const isOpen = open === question.id;
          const kind = statedKind(question) ?? "decision";
          const safeguard = kind === "assumed" ? safeguardOf(question) : null;
          // An assumption resting on what Reeve could not read says so (YAG-999).
          const unread = kind === "assumed" ? unreadSourceOf(question) : null;
          // An alternative picked over the recommendation: its own reason.
          const alternative =
            mine && !typed
              ? options.find((option) => !option.rec && option.answer === mine.body)
              : null;
          return (
            <div key={question.id} data-testid="stated-decision" data-kind={kind}>
              <div className={cx("dl", mine && "mine")}>
                <span className="dq">
                  <span className="qq">{question.text}</span>
                  <span className="aa">
                    <span className="by">{mine ? "You chose" : `${askerName} ${VERB[kind]}`}</span>
                    {current}
                  </span>
                  {alternative?.why && (
                    <span className="cw" data-testid="stated-chosen-why">
                      {alternative.why}
                    </span>
                  )}
                  {mine && (
                    <PlanInputStatus
                      input={mine}
                      askerName={askerName}
                      onRetry={(inputId) => onRetry?.(inputId)}
                      onStartOver={(input) => onStartOver?.(input)}
                      onKeepAsDecision={onKeepAsDecision}
                    />
                  )}
                  {!mine && kind === "checked" && (
                    <span className="src">
                      <CheckedLines question={question} askerName={askerName} />
                    </span>
                  )}
                  {!mine && safeguard && (
                    <span className="guard" data-testid="stated-safeguard">
                      <b>Checked first</b>
                      {safeguard}
                    </span>
                  )}
                  {!mine && unread !== null && (
                    <span className="guard" data-testid="stated-unread-source">
                      <b>Source not read</b>
                      {unread}
                    </span>
                  )}
                </span>
                {onAsk && (
                  <Btn variant="quiet" size="sm" onClick={() => onAsk(question)}>
                    Ask
                  </Btn>
                )}
                {mine && !isOpen ? (
                  <Btn
                    variant="quiet"
                    size="sm"
                    disabled={isPendingInput(mine)}
                    onClick={() => {
                      setDraft(question.id, null);
                      onUndo(question.id);
                    }}
                  >
                    Undo
                  </Btn>
                ) : (
                  <Btn
                    variant="quiet"
                    size="sm"
                    aria-expanded={isOpen}
                    onClick={() => {
                      // Closing leaves what was saved; an unsaved draft is dropped.
                      if (isOpen) setDraft(question.id, null);
                      setOpen(isOpen ? null : question.id);
                    }}
                  >
                    {isOpen ? "Close" : "Change"}
                  </Btn>
                )}
              </div>
              {isOpen && (
                <div className="chg">
                  <div className="opts" role="radiogroup" aria-label={question.text}>
                    {options.map((option) => (
                      <button
                        key={option.answer}
                        type="button"
                        role="radio"
                        aria-checked={current === option.answer}
                        className={cx("opt", current === option.answer && "on")}
                        onClick={() => {
                          setDraft(question.id, null);
                          // Back to the recommendation is back to Reeve's decision.
                          if (option.rec) {
                            if (mine) onUndo(question.id);
                          } else if (mine?.body !== option.answer) {
                            onAnswer(question, option.answer, option.why);
                          }
                          setOpen(null);
                        }}
                      >
                        <span className="rad" aria-hidden />
                        <OptionText option={option} />
                        {option.rec && <span className="rec">recommended</span>}
                      </button>
                    ))}
                    <div className={cx("opt", "other", (typed || typedText.trim()) && "on")}>
                      <Textarea
                        placeholder="Something else"
                        aria-label="Something else"
                        value={typedText}
                        onChange={(event) => setDraft(question.id, event.target.value)}
                        rows={1}
                        autosize
                        className={OPTION_TEXTAREA}
                      />
                    </div>
                  </div>
                  {canSave && (
                    <div className="qacts">
                      <span className="spacer" />
                      <Btn
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          onAnswer(question, draft ?? "", "");
                          setDraft(question.id, null);
                          setOpen(null);
                        }}
                      >
                        {PLAN_ANSWER_SAVE_VERB}
                      </Btn>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
